import React, { useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useTranslations } from '@libs/translations';
import { Button } from 'planoplan-ui-kit/core/button';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { useMediaQuery } from 'planoplan-ui-kit/core/hooks/useMediaQuery';
import { deviceDetected } from 'planoplan-ui-kit/core/device-detected';
import { Points } from '@styles';
import { DownloadButton } from './organism/download-button';

const Logo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80" fill="none">
    <rect width="80" height="80" rx="18.6667" fill="#F2E9E2" />
    <path
      d="M35.1779 12.0925C38.5355 10.1517 42.6828 10.194 46.0002 12.2028L66.7687 24.779C68.3809 25.7554 69.2315 27.3755 69.3234 29.0319L69.3341 29.0212V48.0407L69.3312 48.0427C69.3176 49.7873 68.4644 51.5263 66.7687 52.5534L58.6681 57.4587V44.0407L52.0021 48.0407V61.4958L46.0002 65.1306C42.6831 67.1391 38.5362 67.1812 35.1789 65.2409L13.3254 52.6091C11.5432 51.579 10.6579 49.7719 10.6681 47.9694V29.3747C10.6541 27.5685 11.5395 25.7566 13.3254 24.7243L35.1779 12.0925ZM44.6242 33.1472C42.1363 31.6407 39.026 31.6099 36.508 33.0651L26.8156 38.6657L36.507 44.2683C39.0251 45.7238 42.1352 45.6917 44.6232 44.1853L53.7375 38.6657L44.6242 33.1472ZM44.6232 14.4792C42.1352 12.9728 39.025 12.9407 36.507 14.3962L14.6545 27.028C12.8825 28.0523 12.8825 30.6122 14.6545 31.6364L24.1574 37.1296L35.1789 30.7605C38.5363 28.8202 42.6831 28.8622 46.0002 30.8708L56.3058 37.1101L65.3927 31.6091C67.1022 30.5737 67.1023 28.0907 65.3927 27.0554L44.6232 14.4792Z"
      fill="#2B35EE"
    />
  </svg>
);

export const FirstProject = () => {
  const translations = useTranslations();
  const isTabletPortrait = useMediaQuery(Points.tablet_landscape, { useSSR: true });
  const isDesktopWide = useMediaQuery(Points.laptop_wide, { useSSR: true });
  const isMobileDevice = deviceDetected.isMobile;
  const { locale } = useSelector((state) => state.settings);

  return (
    <View id="first-project">
      <Content>
        <Logo />
        <Title>{translations.t('site.home.first-project.title')}</Title>
        <ButtonWrapper>
          {isMobileDevice ? (
            <Wrapper>
              {locale === 'ru' && (
                <Button
                  theme="primary"
                  data-gtm="button_click"
                  size={isDesktopWide ? 'L' : isTabletPortrait ? 'M' : 'S'}
                  href={`/${locale}/webinars`}>
                  {translations.t('site.home.firstscreen.mobileBtnWebinar')}
                </Button>
              )}
              <Button
                theme={locale === 'ru' ? 'secondary' : 'primary'}
                data-gtm="button_click"
                size={isDesktopWide ? 'L' : isTabletPortrait ? 'M' : 'S'}
                href={`/${locale}/download`}>
                {translations.t('site.home.firstscreen.mobileBtnDownload')}
              </Button>
            </Wrapper>
          ) : (
            <DownloadButton />
          )}
          <Caption>{translations.t('site.home.first-project.caption')}</Caption>
        </ButtonWrapper>
      </Content>
    </View>
  );
};

const View = styled.section`
  position: relative;
  height: 100vh;
  max-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  background-color: ${Colors.main_black};

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('/images/first-project-bg-mob.jpg');
    background-repeat: no-repeat;
    background-size: cover;
    background-position: 50% 50%;
    opacity: 0.1;
  }

  @media ${Points.tablet_landscape} {
    height: 600px;
    padding: 60px 28px 60px;

    &::after {
      background-position: 50% 0;
    }
  }

  @media ${Points.laptop_wide} {
    height: 600px;
    padding: 130px 28px 168px;

    &::after {
      background-image: url('/images/first-project-bg-mob.jpg');
      background-position: 50% 0;
    }
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const ButtonWrapper = styled.div`
  width: 100%;

  @media ${Points.tablet_landscape} {
    margin: 0 auto;
  }

  @media ${Points.laptop_wide} {
    width: max-content;
  }
`;

const Title = styled.h3`
  width: 310px;
  color: ${Colors.white};
  font-weight: 400;
  font-size: 22px;
  leading-trim: NONE;
  line-height: 1.1;
  letter-spacing: 0%;
  text-align: center;
  font-variant-numeric-fraction: stacked-fractions;

  @media ${Points.tablet_landscape} {
    width: 504px;
    font-size: 36px;
  }

  @media ${Points.laptop_wide} {
    width: 504px;
    font-size: 36px;
  }
`;

const Caption = styled.p`
  margin-top: 15px;
  text-align: center;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.1;
  letter-spacing: 1%;
  color: ${Colors.white};

  @media ${Points.tablet_landscape} {
    font-size: 14px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  & a {
    border: none;
  }

  @media ${Points.tablet_portrait} {
    width: 280px;
  }
`;
